import dayjs from "dayjs";
import { formatHotelPortalCancellationDisplayDate } from "../../../helpers/dateHelper";
import { isMasterMemberAgent } from "../../../pages/Hotels/hotelMasterMember";
import {
  resolveHotelBookingCancellationDeadlineFromRooms,
  toHotelApiCancellationCalendarYmd,
} from "../../../pages/Hotels/hotelCancellation";

const INDIA_COUNTRY_TOKENS = new Set(["india", "in", "ind", "indian"]);

const normalizeCountryToken = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "");

const isPhoneDialToken = (value) => {
  const token = String(value ?? "").trim();
  return /^\+?\d{1,4}$/.test(token);
};

const isIndiaCountryToken = (value) => {
  const token = normalizeCountryToken(value);
  if (!token) {
    return false;
  }
  if (INDIA_COUNTRY_TOKENS.has(token)) {
    return true;
  }
  if (token === "91" || token === "+91") {
    return true;
  }
  return /\bindia\b/.test(token);
};

const isNonIndiaCountryToken = (value) => {
  const token = normalizeCountryToken(value);
  if (!token || isPhoneDialToken(token)) {
    return false;
  }
  if (isIndiaCountryToken(token)) {
    return false;
  }
  if (/^[a-z]{2,3}$/.test(token)) {
    return token !== "in" && token !== "ind";
  }
  return token.length > 0;
};

const collectHotelDestinationCountryHints = (ticketData = {}) => {
  const hints = [];
  const push = (value) => {
    const text = String(value ?? "").trim();
    if (text) {
      hints.push(text);
    }
  };

  push(ticketData?.hotelCountryCode);
  push(ticketData?.HotelCountryCode);
  push(ticketData?.hotelCountryName);
  push(ticketData?.HotelCountryName);
  push(ticketData?.destinationCountryCode);
  push(ticketData?.destinationCountryName);

  const addr = ticketData?.HotelAddress;
  if (addr && typeof addr === "object") {
    push(addr.countryCode);
    push(addr.CountryCode);
    push(addr.country);
    push(addr.Country);
    push(addr.countryName);
    push(addr.CountryName);
  }

  let searchReq = ticketData?.searchReq;
  if (!searchReq && ticketData?.Request) {
    try {
      searchReq = JSON.parse(ticketData.Request);
    } catch {
      searchReq = null;
    }
  }
  if (searchReq && typeof searchReq === "object") {
    push(searchReq.hotelCountryCode);
    push(searchReq.hotelCountryName);
    push(searchReq.destinationCountryCode);
    push(searchReq.destinationCountryName);
  }

  const addresses = ticketData?.addresses;
  if (Array.isArray(addresses) && addresses[0]) {
    const first = addresses[0];
    push(first.countryCode);
    push(first.CountryCode);
    push(first.country);
    push(first.Country);
    push(first.countryName);
    push(first.CountryName);
  }

  return hints;
};

/** Hide Tripjack "1 x …" prefix for single room; keep 2+ room combo labels. */
export function formatRoomComboTitle(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return trimmed;
  const match = trimmed.match(/^(\d+)\s*[x×X]\s*(.+)$/);
  if (match) {
    const qty = Math.max(1, Number(match[1]) || 1);
    const name = match[2].trim();
    if (qty <= 1) return name;
    return `${qty} x ${name}`;
  }
  return trimmed;
}

/** Optional guest special request saved on hotel block Request JSON. */
export function getHotelSpecialRequestFromTicket(ticketData = {}) {
  const direct = String(
    ticketData?.specialRequest ?? ticketData?.SpecialRequest ?? "",
  ).trim();
  if (direct) {
    return direct;
  }
  if (ticketData?.Request) {
    try {
      const req =
        typeof ticketData.Request === "string"
          ? JSON.parse(ticketData.Request)
          : ticketData.Request;
      return String(req?.specialRequest ?? req?.SpecialRequest ?? "").trim();
    } catch {
      return "";
    }
  }
  return "";
};

/** Hotel outside India — PAN required for voucher (RBI/TCS). Uses country code/name, not city lists. */
export function isInternationalHotelBooking(ticketData = {}) {
  if (resolveHotelBookingPanRequired(ticketData) === true) {
    return true;
  }

  const hints = collectHotelDestinationCountryHints(ticketData);
  let sawIndia = false;
  let sawNonIndia = false;
  hints.forEach((hint) => {
    if (isIndiaCountryToken(hint)) {
      sawIndia = true;
    } else if (isNonIndiaCountryToken(hint)) {
      sawNonIndia = true;
    }
  });
  if (sawNonIndia) {
    return true;
  }
  if (sawIndia) {
    return false;
  }

  const addr = ticketData?.HotelAddress;
  if (typeof addr === "string") {
    const lower = addr.toLowerCase().trim();
    if (!lower) {
      return false;
    }
    if (/\bindia\b/.test(lower)) {
      return false;
    }
    return true;
  }

  if (addr && typeof addr === "object") {
    const line = String(
      addr.address ?? addr.addressLine ?? addr.Address ?? "",
    )
      .toLowerCase()
      .trim();
    if (/\bindia\b/.test(line)) {
      return false;
    }
    if (line) {
      return true;
    }
  }

  return false;
}

export function guestHasPan(guest) {
  return Boolean(String(guest?.Pan ?? guest?.pan ?? "").trim());
}

/** India guest nationality (ISO IN / Tripjack id 106). Empty → treat as India. */
export function isIndiaGuestNationality(nationality) {
  const raw = String(nationality ?? "")
    .trim()
    .toUpperCase();
  if (!raw) return true;
  return (
    raw === "IN" ||
    raw === "IND" ||
    raw === "INDIA" ||
    raw === "106"
  );
}

export function guestHasPassport(guest) {
  return Boolean(
    String(
      guest?.passportNo ??
        guest?.PassportNo ??
        guest?.passportNumber ??
        guest?.PassportNumber ??
        guest?.passport ??
        "",
    ).trim(),
  );
}

/** Build panvarify passport-cache payloads from review/checkout guest rooms. */
export function collectPassportCachePayloads(guestsRooms = []) {
  const payloads = [];
  const seen = new Set();
  (Array.isArray(guestsRooms) ? guestsRooms : []).forEach((room) => {
    const list = Array.isArray(room?.paxInfoList)
      ? room.paxInfoList
      : Array.isArray(room)
        ? room
        : room && typeof room === "object"
          ? [room]
          : [];
    list.forEach((pax) => {
      const passport_number = String(
        pax?.passportNo ??
          pax?.PassportNo ??
          pax?.passportNumber ??
          pax?.PassportNumber ??
          pax?.passport ??
          "",
      )
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");
      if (!passport_number || passport_number.length < 5) {
        return;
      }
      if (seen.has(passport_number)) {
        return;
      }
      seen.add(passport_number);
      const first_name = String(
        pax?.firstName ?? pax?.FirstName ?? "",
      ).trim();
      const last_name = String(pax?.lastName ?? pax?.LastName ?? "").trim();
      const title = String(pax?.title ?? pax?.Title ?? "").trim();
      payloads.push({
        passport_number,
        first_name,
        last_name,
        title,
        pan_returned_name: [first_name, last_name].filter(Boolean).join(" "),
      });
    });
  });
  return payloads;
}

export function resolveCheckoutNationality(checkoutData = {}) {
  return (
    checkoutData?.hotelSearchData?.nationality ??
    checkoutData?.hotelPriceData?.request?.nationality ??
    checkoutData?.hotelPriceData?.nationality ??
    checkoutData?.nationality ??
    "IN"
  );
}

/** Non-India search nationality → passport docs (no PAN KYC). */
export function isPassportRequiredByNationality(nationality) {
  return !isIndiaGuestNationality(nationality);
}

/**
 * Agent hotel-review: collect passport when search nationality is non-India
 * and any adult still lacks passportNo (no verify API — plain capture).
 */
export function isPassportRequiredForHotelReview(
  checkoutData = {},
  { isAgent = false, membership = null } = {},
) {
  if (!isAgent) {
    return false;
  }
  if (isMasterMemberAgent(5, membership)) {
    return false;
  }
  if (
    !isPassportRequiredByNationality(resolveCheckoutNationality(checkoutData))
  ) {
    return false;
  }
  const flatGuests = flattenCheckoutReviewGuests(checkoutData);
  if (flatGuests.length === 0) {
    return false;
  }
  return flatGuests.some(
    (g) => isHotelAdultGuest(g) && !guestHasPassport(g),
  );
}

/**
 * Explicit Tripjack rate PAN flag (ipr / panRequired).
 * true = required, false = optional, null = unknown → keep nationality/intl fallback.
 * Rate / roomPlan flags must win over guestRequiredFields (nationality-wide pan often wrong).
 */
export function resolveHotelBookingPanRequired(source = {}) {
  if (source?.panRequired === true || source?.PanRequired === true) {
    return true;
  }

  let parsedRequest = null;
  if (typeof source?.Request === "string" && source.Request.trim()) {
    try {
      parsedRequest = JSON.parse(source.Request);
    } catch {
      parsedRequest = null;
    }
  } else if (source?.Request && typeof source.Request === "object") {
    parsedRequest = source.Request;
  }

  const roomPlan = Array.isArray(source?.roomPlan)
    ? source.roomPlan
    : Array.isArray(source?.checkoutRoomPlan)
      ? source.checkoutRoomPlan
      : Array.isArray(parsedRequest?.roomPlan)
        ? parsedRequest.roomPlan
        : Array.isArray(parsedRequest?.checkoutRoomPlan)
          ? parsedRequest.checkoutRoomPlan
          : Array.isArray(source?.hotelSearchData?.roomPlan)
            ? source.hotelSearchData.roomPlan
            : Array.isArray(source?.hotelPriceData?.roomPlan)
              ? source.hotelPriceData.roomPlan
              : [];
  if (roomPlan.length > 0) {
    let sawExplicit = false;
    let anyRequired = false;
    roomPlan.forEach((p) => {
      if (
        p?.panRequired === false ||
        p?.PanRequired === false ||
        p?.ipr === false
      ) {
        sawExplicit = true;
      }
      if (
        p?.panRequired === true ||
        p?.PanRequired === true ||
        p?.ipr === true
      ) {
        sawExplicit = true;
        anyRequired = true;
      }
    });
    if (sawExplicit) {
      return anyRequired;
    }
  }

  if (
    parsedRequest?.panRequired === true ||
    parsedRequest?.PanRequired === true
  ) {
    return true;
  }

  const rooms =
    source?.Rooms ??
    source?.rooms ??
    source?.hotelPriceData?.rooms ??
    source?.hotelPriceData?.Rooms ??
    [];
  if (Array.isArray(rooms) && rooms.length > 0) {
    let sawExplicit = false;
    let anyRequired = false;
    rooms.forEach((room) => {
      if (
        room?.panRequired === false ||
        room?.PanRequired === false ||
        room?.ipr === false
      ) {
        sawExplicit = true;
      }
      if (
        room?.panRequired === true ||
        room?.PanRequired === true ||
        room?.ipr === true
      ) {
        sawExplicit = true;
        anyRequired = true;
      }
      (Array.isArray(room?.ratePlans) ? room.ratePlans : room?.RatePlans ?? []).forEach(
        (rp) => {
          if (
            rp?.panRequired === false ||
            rp?.PanRequired === false ||
            rp?.ipr === false
          ) {
            sawExplicit = true;
          }
          if (
            rp?.panRequired === true ||
            rp?.PanRequired === true ||
            rp?.ipr === true ||
            rp?.pan === true ||
            rp?.PAN === true
          ) {
            sawExplicit = true;
            anyRequired = true;
          }
        },
      );
    });
    if (sawExplicit) {
      return anyRequired;
    }
  }

  // Do not trust ticket.panRequired=false alone — often saved from inverted
  // guestRequiredFields. Explicit optional must come from roomPlan / rate above.
  // Top-level true already returned at the start of this function.

  const guestFields =
    source?.guestRequiredFields ??
    source?.GuestRequiredFields ??
    source?.hotelPriceData?.guestRequiredFields ??
    source?.hotelPriceData?.GuestRequiredFields ??
    [];
  if (Array.isArray(guestFields) && guestFields.length > 0) {
    const anyTrue = guestFields.some(
      (g) => g?.pan === true || g?.PAN === true || g?.Pan === true,
    );
    // Only trust pan=true from guest fields. pan=false here is often nationality-wide
    // and incorrectly skipped PAN Required rates on Pay&Book / Generate Voucher.
    // PAN-optional must come from roomPlan / rate ipr=false above.
    if (anyTrue) {
      return true;
    }
  }

  return null;
}

/** hotel-review: build ticket-like address from checkout session payload. */
export function buildHotelAddressFromCheckoutReview(checkoutData = {}) {
  const hotelPrice = checkoutData?.hotelPriceData ?? {};
  const addresses = Array.isArray(hotelPrice?.addresses) ? hotelPrice.addresses : [];
  const addr0 = addresses[0] ?? {};
  const city = String(
    hotelPrice?.city ??
      hotelPrice?.City ??
      addr0?.cityName ??
      addr0?.CityName ??
      "",
  ).trim();
  const countryCode = String(
    addr0?.countryCode ??
      addr0?.country ??
      addr0?.CountryCode ??
      hotelPrice?.countryCode ??
      hotelPrice?.CountryCode ??
      "",
  ).trim();
  const countryName = String(
    addr0?.countryName ??
      addr0?.CountryName ??
      hotelPrice?.countryName ??
      hotelPrice?.CountryName ??
      "",
  ).trim();
  const addressLine = String(
    addr0?.address ??
      addr0?.Address ??
      hotelPrice?.hotelAddress ??
      hotelPrice?.HotelAddress ??
      "",
  ).trim();
  return {
    HotelAddress: {
      // Include city so Dubai/UAE still detect as international when address blobs are empty.
      address: addressLine || city,
      countryName: countryName || city,
      country: countryCode || countryName,
    },
    CityName: city,
  };
}

export function isInternationalHotelCheckoutReview(checkoutData = {}) {
  return isInternationalHotelBooking(
    buildHotelAddressFromCheckoutReview(checkoutData),
  );
}

export function flattenCheckoutReviewGuests(checkoutData = {}) {
  const rooms = Array.isArray(checkoutData?.guests) ? checkoutData.guests : [];
  const flat = [];
  rooms.forEach((room) => {
    (room?.paxInfoList ?? []).forEach((pax) => {
      flat.push({
        FirstName: pax?.firstName,
        LastName: pax?.lastName,
        firstName: pax?.firstName,
        lastName: pax?.lastName,
        guestType: pax?.guestType,
        GuestType: pax?.GuestType ?? pax?.guestType,
        Pan: pax?.pan ?? pax?.Pan ?? "",
        pan: pax?.pan ?? pax?.Pan ?? "",
        passportNo:
          pax?.passportNo ??
          pax?.PassportNo ??
          pax?.passport ??
          "",
        PassportNo:
          pax?.passportNo ??
          pax?.PassportNo ??
          pax?.passport ??
          "",
      });
    });
  });
  return flat;
}

/** B2B hotel-review Pay and Book: same rule as Generate Voucher on intl tickets.
 *  Skip when selected rate is PAN Optional — same as Master.
 *  Explicit panRequired=true from Details roomPlan / priced rooms always asks PAN.
 *  Domestic: never collect PAN on Review (agent Review UI is International-only). */
export function isPanRequiredForHotelReview(checkoutData = {}, { isAgent = false, membership = null } = {}) {
  if (isAgent && isMasterMemberAgent(5, membership)) {
    return false;
  }
  if (!isAgent) {
    return false;
  }
  if (isPassportRequiredByNationality(resolveCheckoutNationality(checkoutData))) {
    return false;
  }
  const panReq = resolveHotelBookingPanRequired({
    ...checkoutData,
    roomPlan: checkoutData?.hotelSearchData?.roomPlan,
    rooms: checkoutData?.hotelPriceData?.rooms,
    guestRequiredFields: checkoutData?.hotelPriceData?.guestRequiredFields,
  });
  // Without PAN rate — Instant/Hold without PAN UI.
  if (panReq === false) {
    return false;
  }
  // Domestic: only when Details selected PAN Required (Pay & Book Review modal — not checkout guest form).
  if (!isInternationalHotelCheckoutReview(checkoutData)) {
    if (panReq !== true) {
      return false;
    }
  }
  const flatGuests = flattenCheckoutReviewGuests(checkoutData);
  if (flatGuests.length === 0) {
    return false;
  }
  return !flatGuests.some(guestHasPan);
}

/** Agent hotel-review with 2+ rooms: per-room PAN before Pay & Book / PG. */
export function isMultiRoomPanRequiredOnHotelReview(
  checkoutData = {},
  { isAgent = false, membership = null } = {},
) {
  if (isAgent && isMasterMemberAgent(5, membership)) {
    return false;
  }
  if (!isAgent) {
    return false;
  }
  if (isPassportRequiredByNationality(resolveCheckoutNationality(checkoutData))) {
    return false;
  }
  const roomPlan = checkoutData?.hotelSearchData?.roomPlan;
  let planPan = null;
  if (Array.isArray(roomPlan) && roomPlan.length > 0) {
    const flags = roomPlan
      .map((p) =>
        p?.panRequired === true || p?.PanRequired === true
          ? true
          : p?.panRequired === false || p?.PanRequired === false
            ? false
            : null,
      )
      .filter((v) => v !== null);
    if (flags.length > 0) {
      planPan = flags.some((v) => v === true);
    }
  }
  if (planPan === false) {
    return false;
  }
  // Domestic multi-room: only when Details selected PAN Required.
  if (!isInternationalHotelCheckoutReview(checkoutData)) {
    if (planPan !== true) {
      return false;
    }
  }
  const guestFields =
    checkoutData?.hotelPriceData?.guestRequiredFields ??
    checkoutData?.hotelPriceData?.GuestRequiredFields ??
    [];
  if (
    planPan !== true &&
    Array.isArray(guestFields) &&
    guestFields.some((g) => g?.pan === false || g?.PAN === false || g?.Pan === false) &&
    !guestFields.some((g) => g?.pan === true || g?.PAN === true || g?.Pan === true)
  ) {
    return false;
  }
  const rooms =
    checkoutData?.hotelPriceData?.rooms ??
    checkoutData?.hotelPriceData?.Rooms ??
    [];
  if (planPan !== true && Array.isArray(rooms) && rooms.length > 0) {
    let sawExplicit = false;
    let anyRequired = false;
    rooms.forEach((room) => {
      (Array.isArray(room?.ratePlans) ? room.ratePlans : [room]).forEach((rp) => {
        if (
          rp?.panRequired === false ||
          rp?.PanRequired === false ||
          rp?.ipr === false
        ) {
          sawExplicit = true;
        }
        if (
          rp?.panRequired === true ||
          rp?.PanRequired === true ||
          rp?.ipr === true ||
          rp?.pan === true ||
          rp?.PAN === true
        ) {
          sawExplicit = true;
          anyRequired = true;
        }
      });
    });
    if (sawExplicit && !anyRequired) {
      return false;
    }
  }
  const roomGuests = Array.isArray(checkoutData?.guests) ? checkoutData.guests : [];
  if (roomGuests.length <= 1) {
    return false;
  }
  // Int multi-room Pay&Book, or Dom when Details selected PAN Required.
  return (
    isInternationalHotelCheckoutReview(checkoutData) || planPan === true
  );
}

export const HOTEL_REVIEW_MAX_ROOMS_PER_PAN = 6;

export function getHotelReviewRoomsMissingPan(guests = [], samePanForAll = false) {
  if (!Array.isArray(guests) || guests.length === 0) {
    return [];
  }
  const roomHasPanGuest = (room) =>
    (room?.paxInfoList ?? []).some((pax) => guestHasPan(pax));

  if (samePanForAll) {
    return guests.some(roomHasPanGuest)
      ? []
      : guests.map((_, idx) => idx + 1);
  }

  return guests.reduce((missing, room, idx) => {
    if (!roomHasPanGuest(room)) {
      missing.push(idx + 1);
    }
    return missing;
  }, []);
}

export function formatHotelReviewMissingPanMessage(missingRooms = []) {
  if (!missingRooms.length) {
    return "";
  }
  if (missingRooms.length === 1) {
    return `Please provide PAN number for Room ${missingRooms[0]}.`;
  }
  return `Please provide PAN number for Room ${missingRooms.join(", ")}.`;
}

/** Inline errors for multi-room Generate Voucher — one message per room missing PAN. */
export function buildHotelVoucherPanFieldErrors(
  ticketData = {},
  { samePanForAll = false } = {},
) {
  const grouped = groupHotelTicketGuestsByRoom(ticketData);
  const missingRooms = getHotelReviewRoomsMissingPan(grouped, samePanForAll);
  const errors = {};
  if (!missingRooms.length) {
    return errors;
  }
  if (samePanForAll) {
    errors[String(getHotelReviewPrimaryPanRoomIndex(grouped))] =
      "Please verify Pan Number";
    return errors;
  }
  missingRooms.forEach((roomNo) => {
    errors[String(roomNo - 1)] = "Please verify Pan Number";
  });
  return errors;
}

export function exceedsHotelReviewSamePanRoomLimit(
  roomCount = 0,
  samePanForAll = false,
) {
  return (
    samePanForAll && Number(roomCount) > HOTEL_REVIEW_MAX_ROOMS_PER_PAN
  );
}

export function validateHotelReviewPanBeforeBook(
  guests = [],
  { samePanForAll = false, membership = null } = {},
) {
  if (isMasterMemberAgent(5, membership)) {
    return { ok: true, message: "" };
  }
  const roomCount = guests.length;
  if (exceedsHotelReviewSamePanRoomLimit(roomCount, samePanForAll)) {
    return {
      ok: false,
      message: `One PAN number can be used for maximum ${HOTEL_REVIEW_MAX_ROOMS_PER_PAN} rooms only.`,
    };
  }
  const missingRooms = getHotelReviewRoomsMissingPan(guests, samePanForAll);
  if (missingRooms.length > 0) {
    return {
      ok: false,
      message: formatHotelReviewMissingPanMessage(missingRooms),
    };
  }
  return { ok: true, message: "" };
}

export function eachHotelReviewRoomHasPan(guests = [], samePanForAll = false) {
  return validateHotelReviewPanBeforeBook(guests, { samePanForAll }).ok;
}

export function applyHotelReviewPanToGuests(
  guests = [],
  panValue,
  { samePanForAll = false, roomIndex = 0 } = {},
) {
  return applyHotelReviewPanByNameMatch(guests, panValue, {}, {
    samePanForAll,
    roomIndex,
    panName: "",
    matchAnyAdultInRoom: true,
  });
}

export function applyHotelReviewPanByNameMatch(
  guests = [],
  panValue,
  panMeta = {},
  {
    samePanForAll = false,
    roomIndex = 0,
    panName = "",
    matchAnyAdultInRoom = false,
  } = {},
) {
  const pan = String(panValue ?? "").trim().toUpperCase();
  if (!pan || !Array.isArray(guests) || guests.length === 0) {
    return guests;
  }

  const roomGuests = (guests[roomIndex]?.paxInfoList ?? []).map((pax) => ({
    ...pax,
    FirstName: pax?.firstName ?? pax?.FirstName,
    LastName: pax?.lastName ?? pax?.LastName,
  }));

  let matchedGuest = null;
  if (matchAnyAdultInRoom) {
    matchedGuest =
      roomGuests.find((pax) => guestHasPan(pax)) ??
      (samePanForAll
        ? null
        : roomGuests.find((pax) => isHotelAdultGuest(pax)) ?? roomGuests[0]);
  } else {
    matchedGuest = findGuestMatchingPanName(
      panName,
      roomGuests,
      pan,
      panMeta,
    );
  }

  if (!matchedGuest) {
    return samePanForAll ? guests : null;
  }

  const holder = splitPanHolderNameForGuest(panName, {
    firstName: panMeta?.firstName ?? panMeta?.first_name,
    lastName: panMeta?.lastName ?? panMeta?.last_name,
    panReturnedName:
      panMeta?.panReturnedName ??
      panMeta?.pan_returned_name ??
      panName,
  });
  const matchedFirst = normalizePersonName(
    matchedGuest?.firstName ?? matchedGuest?.FirstName ?? "",
  );
  const matchedLast = normalizePersonName(
    matchedGuest?.lastName ?? matchedGuest?.LastName ?? "",
  );

  // Stamp PAN only on the matched adult (PAN holder). Do NOT put pan on
  // other adults with placeholder surnames (e.g. Rahul SYT) — that blocked
  // hotelblock before the API call ("PAN on placeholder surname").
  const stampMatchedAdultInRoom = (room) => {
    let paxInfoList = (room?.paxInfoList ?? []).map((pax) => {
      const paxFirst = normalizePersonName(
        pax?.firstName ?? pax?.FirstName ?? "",
      );
      const paxLast = normalizePersonName(
        pax?.lastName ?? pax?.LastName ?? "",
      );
      const isMatchedAdult =
        isHotelAdultGuest(pax) &&
        paxFirst === matchedFirst &&
        paxLast === matchedLast;
      if (!isMatchedAdult) {
        // Clear accidental pan on non-holder adults so book guard does not trip.
        if (isHotelAdultGuest(pax) && (pax?.pan || pax?.Pan)) {
          const cleared = { ...pax };
          delete cleared.pan;
          delete cleared.Pan;
          return cleared;
        }
        return pax;
      }
      const next = {
        ...pax,
        pan,
        Pan: pan,
        leadGuest: true,
      };
      const lastRaw = String(pax?.lastName ?? pax?.LastName ?? "").trim();
      if (
        holder.firstName &&
        holder.lastName &&
        (!lastRaw || isHotelGenericGuestLastName(lastRaw))
      ) {
        next.firstName = holder.firstName;
        next.lastName = holder.lastName;
        next.FirstName = holder.firstName;
        next.LastName = holder.lastName;
        next.panHolderName = `${holder.firstName} ${holder.lastName}`.trim();
      }
      return next;
    });
    // Room lead = PAN holder (OMS puts pan on first adult).
    const panIdx = paxInfoList.findIndex(
      (p) =>
        String(p?.pan ?? p?.Pan ?? "")
          .trim()
          .toUpperCase() === pan && isHotelAdultGuest(p),
    );
    if (panIdx > 0) {
      const [lead] = paxInfoList.splice(panIdx, 1);
      paxInfoList = [
        lead,
        ...paxInfoList.map((p) =>
          isHotelAdultGuest(p) ? { ...p, leadGuest: false } : p,
        ),
      ];
    }
    return { ...room, paxInfoList };
  };

  if (samePanForAll) {
    return guests.map((room, idx) => {
      if (idx === roomIndex) {
        return stampMatchedAdultInRoom(room);
      }
      const adults = (room?.paxInfoList ?? []).filter((p) =>
        isHotelAdultGuest(p),
      );
      if (adults.length === 0) {
        return room;
      }
      const lead = adults[0];
      const leadFirst = normalizePersonName(
        lead?.firstName ?? lead?.FirstName ?? "",
      );
      const leadLast = normalizePersonName(
        lead?.lastName ?? lead?.LastName ?? "",
      );
      let paxInfoList = (room?.paxInfoList ?? []).map((pax) => {
        const paxFirst = normalizePersonName(
          pax?.firstName ?? pax?.FirstName ?? "",
        );
        const paxLast = normalizePersonName(
          pax?.lastName ?? pax?.LastName ?? "",
        );
        const isLead =
          isHotelAdultGuest(pax) &&
          paxFirst === leadFirst &&
          paxLast === leadLast;
        if (!isLead) {
          if (isHotelAdultGuest(pax) && (pax?.pan || pax?.Pan)) {
            const cleared = { ...pax };
            delete cleared.pan;
            delete cleared.Pan;
            return cleared;
          }
          return pax;
        }
        const next = { ...pax, pan, Pan: pan, leadGuest: true };
        const lastRaw = String(pax?.lastName ?? pax?.LastName ?? "").trim();
        if (
          holder.firstName &&
          holder.lastName &&
          (!lastRaw || isHotelGenericGuestLastName(lastRaw))
        ) {
          next.firstName = holder.firstName;
          next.lastName = holder.lastName;
          next.FirstName = holder.firstName;
          next.LastName = holder.lastName;
          next.panHolderName = `${holder.firstName} ${holder.lastName}`.trim();
        }
        return next;
      });
      const panIdx = paxInfoList.findIndex(
        (p) =>
          String(p?.pan ?? "")
            .trim()
            .toUpperCase() === pan && isHotelAdultGuest(p),
      );
      if (panIdx > 0) {
        const [leadPax] = paxInfoList.splice(panIdx, 1);
        paxInfoList = [leadPax, ...paxInfoList];
      }
      return { ...room, paxInfoList };
    });
  }

  return guests.map((room, idx) =>
    idx === roomIndex ? stampMatchedAdultInRoom(room) : room,
  );
}

export function formatHotelGuestDisplayName(guest = {}) {
  const title = String(guest?.Title ?? guest?.title ?? "").trim();
  const first = String(guest?.FirstName ?? guest?.firstName ?? "").trim();
  const last = String(guest?.LastName ?? guest?.lastName ?? "").trim();
  return [title, first, last].filter(Boolean).join(" ");
}

export function getHotelReviewGuestsMissingTitle(guests = []) {
  const missingKeys = [];
  if (!Array.isArray(guests)) {
    return missingKeys;
  }
  guests.forEach((room, roomIndex) => {
    (room?.paxInfoList ?? []).forEach((pax, paxIndex) => {
      if (!String(pax?.title ?? pax?.Title ?? "").trim()) {
        missingKeys.push(`${roomIndex}_${paxIndex}`);
      }
    });
  });
  return missingKeys;
}

export function getHotelGuestRoomNumber(guest = {}, fallback = 1) {
  const roomNo = Number(
    guest?.GuestInRoom ??
      guest?.guestInRoom ??
      guest?.RoomSerialNo ??
      guest?.roomSerialNo ??
      fallback,
  );
  return roomNo > 0 ? roomNo : fallback;
}

export function getHotelReviewRoomPanDisplay(guests = [], roomIndex = 0) {
  const room = guests?.[roomIndex];
  const guestWithPan = (room?.paxInfoList ?? []).find((pax) => guestHasPan(pax));
  return String(guestWithPan?.pan ?? guestWithPan?.Pan ?? "")
    .trim()
    .toUpperCase();
}

/** First room (0-based) with a verified PAN on an adult; default 0 if none yet. */
export function getHotelReviewPrimaryPanRoomIndex(guests = []) {
  if (!Array.isArray(guests)) {
    return 0;
  }
  for (let i = 0; i < guests.length; i += 1) {
    const hasPanGuest = (guests[i]?.paxInfoList ?? []).some((pax) =>
      guestHasPan(pax),
    );
    if (hasPanGuest) {
      return i;
    }
  }
  return 0;
}

/** First verified PAN value and the room it was entered in. */
export function findHotelReviewSharedPan(guests = []) {
  if (!Array.isArray(guests)) {
    return { pan: "", roomIndex: 0 };
  }
  for (let i = 0; i < guests.length; i += 1) {
    const pan = getHotelReviewRoomPanDisplay(guests, i);
    if (pan) {
      return { pan, roomIndex: i };
    }
  }
  return { pan: "", roomIndex: 0 };
}

/** Uncheck "same PAN" — keep PAN only on keepRoomIndex, strip from other rooms.
 *  When checking "same PAN", use applyHotelReviewPanByNameMatch(..., { samePanForAll: true })
 *  so each room lead keeps the shared PAN for Tripjack confirm-book.
 */
export function clearHotelReviewPanFromOtherRooms(
  guests = [],
  keepRoomIndex = 0,
) {
  if (!Array.isArray(guests)) {
    return guests;
  }
  return guests.map((room, idx) => {
    if (idx === keepRoomIndex) {
      return room;
    }
    return {
      ...room,
      paxInfoList: (room?.paxInfoList ?? []).map((pax) => {
        const next = { ...pax };
        delete next.pan;
        delete next.Pan;
        return next;
      }),
    };
  });
}

export function getHotelTicketRoomCount(ticketData = {}) {
  const fromNo = Number(ticketData?.NoOfRooms ?? 0);
  const fromRooms = Array.isArray(ticketData?.Rooms) ? ticketData.Rooms.length : 0;
  const guests = Array.isArray(ticketData?.guests) ? ticketData.guests : [];
  const fromGuestInRoom = guests.reduce(
    (max, guest) => Math.max(max, getHotelGuestRoomNumber(guest, 1)),
    0,
  );
  return Math.max(fromNo, fromRooms, fromGuestInRoom, 1);
}

export function getHotelTicketGuestCount(ticketData = {}) {
  const guests = Array.isArray(ticketData?.guests) ? ticketData.guests : [];
  return guests.length;
}

export function groupHotelTicketGuestsByRoom(ticketData = {}) {
  const guests = Array.isArray(ticketData?.guests) ? ticketData.guests : [];
  const roomCount = getHotelTicketRoomCount(ticketData);
  const buckets = {};

  guests.forEach((guest) => {
    const roomNo = getHotelGuestRoomNumber(guest, 1);
    if (!buckets[roomNo]) {
      buckets[roomNo] = [];
    }
    const row = {
      ...guest,
      firstName: guest?.FirstName ?? guest?.firstName ?? "",
      lastName: guest?.LastName ?? guest?.lastName ?? "",
      guestType: guest?.GuestType ?? guest?.guestType ?? "Adult",
      title: guest?.Title ?? guest?.title ?? "",
      guestInRoom: getHotelGuestRoomNumber(guest, roomNo),
      GuestInRoom: getHotelGuestRoomNumber(guest, roomNo),
      RoomSerialNo: guest?.RoomSerialNo ?? guest?.roomSerialNo ?? roomNo,
      pan: guest?.Pan ?? guest?.pan ?? "",
      Pan: guest?.Pan ?? guest?.pan ?? "",
      passportNo:
        guest?.PassportNo ??
        guest?.passportNo ??
        guest?.passport ??
        "",
      PassportNo:
        guest?.PassportNo ??
        guest?.passportNo ??
        guest?.passport ??
        "",
    };
    // Preview has no child PAN/passport; ticket must match (SQL may still store a copied adult value).
    if (!isHotelAdultGuest(row)) {
      row.pan = "";
      row.Pan = "";
      row.passportNo = "";
      row.PassportNo = "";
      delete row.PAN;
      delete row.passport;
    }
    buckets[roomNo].push(row);
  });

  return Array.from({ length: roomCount }, (_, roomIndex) => ({
    paxInfoList: buckets[roomIndex + 1] ?? [],
  }));
}

export function isMultiRoomPanRequiredForHotelVoucher(ticketData = {}) {
  if (shouldShowPassportOnHotelTicket(ticketData)) {
    return false;
  }
  if (resolveHotelBookingPanRequired(ticketData) === false) {
    return false;
  }
  if (getHotelTicketRoomCount(ticketData) <= 1) {
    return false;
  }
  // Explicit Tripjack PAN Required rate (Domestic or International).
  if (resolveHotelBookingPanRequired(ticketData) === true) {
    return true;
  }
  return isInternationalHotelBooking(ticketData);
}

/** Pay & Book / ticket — status CONFIRMED (3). */
export function isHotelConfirmedBooking(ticketData = {}) {
  return Number(ticketData?.BookingStatus) === 3;
}

/** Hold/CONFIRMED + same PAN checked + PAN verified → hide per-room guest/PAN forms. */
export function shouldCollapseHotelSamePanRoomForms(
  ticketData = {},
  { samePanForAll = false } = {},
) {
  if (!samePanForAll || !showHotelTicketMultiRoomPanSection(ticketData)) {
    return false;
  }
  const grouped = groupHotelTicketGuestsByRoom(ticketData);
  return eachHotelReviewRoomHasPan(grouped, true);
}

export function showHotelTicketMultiRoomPanSection(
  ticketData = {},
  { membership = null } = {},
) {
  if (isMasterMemberAgent(5, membership)) {
    return false;
  }
  if (!isMultiRoomPanRequiredForHotelVoucher(ticketData)) {
    return false;
  }
  return (
    isHotelHoldAwaitingVoucher(ticketData) || isHotelConfirmedBooking(ticketData)
  );
}

export function applyHotelTicketPanToGuests(
  guests = [],
  panValue,
  { samePanForAll = false, roomIndex = 0 } = {},
) {
  const pan = String(panValue ?? "").trim().toUpperCase();
  if (!pan) {
    return guests;
  }
  const targetRoomNo = roomIndex + 1;
  return guests.map((guest) => {
    const roomNo = getHotelGuestRoomNumber(guest, 1);
    if (samePanForAll || roomNo === targetRoomNo) {
      return { ...guest, Pan: pan, pan };
    }
    return guest;
  });
}

export function validateHotelTicketVoucherPan(
  ticketData = {},
  { samePanForAll = false, membership = null } = {},
) {
  if (isMasterMemberAgent(5, membership)) {
    return { ok: true, message: "" };
  }
  if (shouldShowPassportOnHotelTicket(ticketData)) {
    return { ok: true, message: "" };
  }
  if (resolveHotelBookingPanRequired(ticketData) === false) {
    return { ok: true, message: "" };
  }
  const roomCount = getHotelTicketRoomCount(ticketData);
  if (exceedsHotelReviewSamePanRoomLimit(roomCount, samePanForAll)) {
    return {
      ok: false,
      message: `One PAN number can be used for maximum ${HOTEL_REVIEW_MAX_ROOMS_PER_PAN} rooms only.`,
    };
  }
  return validateHotelReviewPanBeforeBook(
    groupHotelTicketGuestsByRoom(ticketData),
    { samePanForAll, membership },
  );
}

export function isHotelPaidVoucheredForRefund(ticketData = {}) {
  const status = Number(ticketData?.BookingStatus ?? 0);
  const pgPaid = Number(ticketData?.pgStatus ?? 0) === 1;
  // Hold (10 CONFIRMED) never took voucher money — refund toast is VOUCHERED only.
  return pgPaid && (status === 11 || status === 3);
}

export function resolveHotelCancelRefundAmount(ticketData = {}, resData = {}) {
  const pgPaid = Number(ticketData?.pgStatus ?? 0) === 1;
  const walletRefunded = Boolean(resData?.walletRefunded);
  const hasExplicitRefund =
    resData != null &&
    (Object.prototype.hasOwnProperty.call(resData, "refundAmount") ||
      Object.prototype.hasOwnProperty.call(resData, "walletRefundAmount") ||
      Object.prototype.hasOwnProperty.call(resData, "walletRefunded"));
  const walletAmt = Number(resData?.walletRefundAmount ?? 0);
  const listedRefund = Number(resData?.refundAmount ?? 0);
  // walletRefundAmount=0 after an already-credited ledger must not hide refundAmount.
  const fromApi = walletAmt > 0 ? walletAmt : listedRefund;
  // Unpaid Hold cancel: never invent ticket total as "refund".
  if (!pgPaid && !walletRefunded) {
    return 0;
  }
  // Cancel API said refund 0 (Non-refundable) — do not invent payable as refund toast.
  if (hasExplicitRefund && !(fromApi > 0) && !walletRefunded) {
    return 0;
  }
  if (fromApi > 0) {
    return Math.round(fromApi);
  }
  if (!pgPaid) {
    return 0;
  }
  // No cancel payload — do not invent fare as refund (NR / charges cases).
  if (hasExplicitRefund) {
    return 0;
  }
  return 0;
}

export function getEffectiveHotelBookingStatus(ticketData = {}) {
  const status = Number(ticketData?.BookingStatus ?? 0);
  if (status === 6) {
    return 6;
  }
  if (Number(ticketData?.cancellationStatus) === 6) {
    return 6;
  }
  const refundAmt = Number(ticketData?.refundAmount ?? 0);
  if (Number(ticketData?.pgStatus) === 1 && refundAmt > 0) {
    return 6;
  }
  return status;
}

/** Apply cancel API result to ticket state — no page refresh needed. */
export function buildHotelTicketAfterCancelPatch(
  ticketData = {},
  cancelData = {},
  cancelMeta = {},
) {
  const pgPaid = Number(ticketData?.pgStatus ?? 0) === 1;
  const refundAmt = resolveHotelCancelRefundAmount(ticketData, cancelData);
  const walletRefundAmount = Number(
    cancelData?.walletRefundAmount ??
      (pgPaid ? cancelData?.refundAmount ?? refundAmt : 0) ??
      0,
  );
  return {
    ...ticketData,
    BookingStatus: 6,
    cancellationStatus: 6,
    cancellable: false,
    refundAmount: pgPaid ? refundAmt : 0,
    walletRefundAmount: pgPaid ? walletRefundAmount : 0,
    walletRefunded:
      Boolean(cancelData?.walletRefunded) ||
      (pgPaid && walletRefundAmount > 0),
    cancelationRemarks:
      cancelMeta?.cancelationRemarks ??
      cancelData?.cancelationRemarks ??
      ticketData?.cancelationRemarks ??
      "",
  };
}

export function isHotelCancelApiSuccess(res = {}) {
  const httpOk = Number(res?.status ?? res?.statusCode ?? 0) === 200;
  if (!httpOk) {
    return false;
  }
  const bookingStatus = String(res?.data?.bookingStatus ?? "").toLowerCase();
  if (bookingStatus === "cancelled" || bookingStatus === "canceled") {
    return true;
  }
  return httpOk && res?.data != null && !Array.isArray(res.data);
}

export const HOTEL_CANCELLATION_REASON_OPTIONS = [
  "Found a Better Price Elsewhere",
  "Visa Rejected / Delayed",
  "Client Requested Date Change / Amendment",
  "Other (Please Specify/Remarks)",
];

export const HOTEL_CANCELLATION_OTHER_REASON =
  "Other (Please Specify/Remarks)";

export function serializeHotelCancelRemarksForApi(reasons = [], remarks = "") {
  return JSON.stringify({
    reasons: (Array.isArray(reasons) ? reasons : [])
      .map((item) => String(item ?? "").trim())
      .filter(Boolean),
    remarks: String(remarks ?? "").trim(),
  });
}

export function parseHotelCancellationDisplay(ticketData = {}) {
  const raw = String(
    ticketData?.cancelationRemarks ??
      ticketData?.Cancelationremarks ??
      ticketData?.cancellationRemarks ??
      "",
  ).trim();
  if (!raw) {
    return { reasons: [], remarks: "", lines: [] };
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && Array.isArray(parsed.reasons)) {
      const reasons = parsed.reasons
        .map((item) => String(item ?? "").trim())
        .filter(Boolean);
      const remarks = String(parsed.remarks ?? "").trim();
      return { reasons, remarks, lines: [] };
    }
  } catch {
    // Legacy plain-text remarks.
  }
  return { reasons: [], remarks: raw, lines: [raw] };
}

export function getHotelCancellationReasonLines(ticketData = {}) {
  const parsed = parseHotelCancellationDisplay(ticketData);
  if (parsed.lines.length > 0) {
    return parsed.lines;
  }
  const lines = [...parsed.reasons];
  const remarks = String(parsed.remarks ?? "").trim();
  if (remarks) {
    lines.push(remarks);
  }
  return lines;
}

export const normalizePersonName = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

export const getGuestDisplayName = (guest = {}) =>
  normalizePersonName(
    `${guest?.FirstName ?? guest?.firstName ?? ""} ${
      guest?.LastName ?? guest?.lastName ?? ""
    }`,
  );

export const tokenizePersonName = (value) =>
  normalizePersonName(value)
    .split(" ")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

/** Placeholder / agency surnames on hold tickets — match PAN on first name. */
const GENERIC_GUEST_LAST_NAMES = new Set([
  "syt",
  "guest",
  "name",
  "test",
  "user",
  "na",
  "xxx",
  "traveler",
  "traveller",
]);

const isGenericGuestLastName = (lastName) =>
  GENERIC_GUEST_LAST_NAMES.has(normalizePersonName(lastName));

/** Export for Review modal — replace SYT/placeholder with PAN holder name before OMS. */
export function isHotelGenericGuestLastName(lastName) {
  return isGenericGuestLastName(lastName);
}

/**
 * Split PAN-returned full name into first/last for Tripjack OMS
 * (surname 1st letter must align with PAN 5th character).
 */
export function splitPanHolderNameForGuest(panName = "", panMeta = {}) {
  const metaFirst = String(panMeta?.firstName ?? "").trim();
  const metaLast = String(panMeta?.lastName ?? "").trim();
  if (metaFirst && metaLast) {
    return { firstName: metaFirst, lastName: metaLast };
  }
  const raw = String(
    panMeta?.panReturnedName ?? panName ?? "",
  )
    .trim()
    .replace(/\s+/g, " ");
  if (!raw) {
    return { firstName: "", lastName: "" };
  }
  const parts = raw.split(" ").filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: parts[0] };
  }
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

const buildPanHolderNameCandidates = (panName, panMeta = {}) => {
  const candidates = new Set();
  const add = (value) => {
    const normalized = normalizePersonName(value);
    if (normalized) {
      candidates.add(normalized);
    }
  };

  add(panName);
  add(panMeta?.panReturnedName);
  add(`${panMeta?.firstName ?? ""} ${panMeta?.lastName ?? ""}`);
  add(`${panMeta?.lastName ?? ""} ${panMeta?.firstName ?? ""}`);
  add(panMeta?.firstName);
  add(panMeta?.lastName);

  return [...candidates];
};

/**
 * PAN holder name vs ticket adult — exact, token, or first-name match
 * (hold tickets often use placeholder surnames like SYT).
 */
export const doesPanNameMatchGuest = (panName, guest = {}, panMeta = {}) => {
  const guestFirst = normalizePersonName(
    guest?.FirstName ?? guest?.firstName ?? "",
  );
  const guestLast = normalizePersonName(
    guest?.LastName ?? guest?.lastName ?? "",
  );
  const guestFull = getGuestDisplayName(guest);
  if (!guestFirst && !guestFull) {
    return false;
  }

  const panCandidates = buildPanHolderNameCandidates(panName, panMeta);
  if (panCandidates.length === 0) {
    return false;
  }

  for (const panFull of panCandidates) {
    if (guestFull && panFull === guestFull) {
      return true;
    }

    const panTokens = tokenizePersonName(panFull);
    const guestTokens = tokenizePersonName(guestFull);

    if (
      guestTokens.length > 0 &&
      guestTokens.every((token) => panTokens.includes(token))
    ) {
      return true;
    }

    const panFirst = normalizePersonName(
      panMeta?.firstName ?? panTokens[0] ?? "",
    );
    const panLast = normalizePersonName(
      panMeta?.lastName ?? panTokens[panTokens.length - 1] ?? "",
    );

    if (guestFirst && panFirst && guestFirst === panFirst) {
      if (!guestLast || isGenericGuestLastName(guestLast)) {
        return true;
      }
      if (guestLast && (panTokens.includes(guestLast) || panLast === guestLast)) {
        return true;
      }
    }

    if (
      guestFirst &&
      guestFirst.length >= 3 &&
      panTokens.includes(guestFirst) &&
      (!guestLast || isGenericGuestLastName(guestLast))
    ) {
      return true;
    }

    if (
      guestFirst &&
      guestLast &&
      !isGenericGuestLastName(guestLast) &&
      panTokens.includes(guestFirst) &&
      panTokens.includes(guestLast)
    ) {
      return true;
    }
  }

  return false;
};

export const isLeadHotelGuest = (guest = {}) =>
  String(guest?.LeadGuest ?? guest?.leadGuest ?? "") === "1" ||
  guest?.LeadGuest === 1 ||
  guest?.leadGuest === 1 ||
  guest?.leadGuest === true;

/** One PAN per hold — match only the lead guest, not every passenger. */
export const getLeadHotelGuest = (guests = []) => {
  const list = Array.isArray(guests) ? guests : [];
  if (list.length === 0) {
    return null;
  }
  return list.find((guest) => isLeadHotelGuest(guest)) ?? list[0];
};

export const isHotelAdultGuest = (guest = {}) => {
  const guestType = String(
    guest?.GuestType ?? guest?.guestType ?? "Adult",
  ).toLowerCase();
  return (
    guestType === "adult" ||
    guestType === "1" ||
    Number(guest?.GuestType ?? guest?.guestType) === 1
  );
};

export const getHotelAdultGuests = (guests = []) =>
  (Array.isArray(guests) ? guests : []).filter(isHotelAdultGuest);

/**
 * PAN holder name may match any guest in the list (adult or child).
 * Adults are checked first; then children — e.g. child PAN entered on room lead row.
 */
export const findGuestMatchingPanName = (
  panName,
  guests = [],
  panNumber = "",
  panMeta = {},
) => {
  const list = Array.isArray(guests) ? guests : [];
  const panUpper = String(panNumber ?? "").trim().toUpperCase();
  if (panUpper) {
    const byPan = list.find(
      (guest) =>
        String(guest?.Pan ?? guest?.pan ?? "").trim().toUpperCase() === panUpper,
    );
    if (byPan) {
      return byPan;
    }
  }
  const adults = getHotelAdultGuests(list);
  const adultMatch = adults.find((guest) =>
    doesPanNameMatchGuest(panName, guest, panMeta),
  );
  if (adultMatch) {
    return adultMatch;
  }
  return (
    list.find((guest) => doesPanNameMatchGuest(panName, guest, panMeta)) ??
    null
  );
};

/** @deprecated Use findGuestMatchingPanName — kept for existing imports. */
export const findAdultGuestMatchingPanName = findGuestMatchingPanName;

export const isPanNameMatchedWithAnyAdultGuest = (
  panName,
  guests = [],
  panNumber = "",
  panMeta = {},
) => Boolean(findGuestMatchingPanName(panName, guests, panNumber, panMeta));

/** @deprecated Use isPanNameMatchedWithAnyAdultGuest */
export const isPanNameMatchedWithLeadGuest = (panName, guests = []) =>
  isPanNameMatchedWithAnyAdultGuest(panName, guests);

/** Show Pan No. column on ticket — international bookings and when PAN is stored.
 *  India nationality → always this path (never Passport Number column). */
export function shouldShowPanOnHotelTicket(ticketData = {}) {
  // Non-India nationality / passport booking → Passport Number column instead.
  if (shouldShowPassportOnHotelTicket(ticketData)) {
    return false;
  }
  const guests = Array.isArray(ticketData?.guests) ? ticketData.guests : [];
  if (guests.some(guestHasPan)) {
    return true;
  }
  if (resolveHotelBookingPanRequired(ticketData) === true) {
    return true;
  }
  return isInternationalHotelBooking(ticketData);
}

/**
 * Passport Number on hotel ticket only when search nationality is NOT India.
 * Explicit India (IN / 106) keeps classic Pan No. UI — unchanged.
 * Missing nationality: show passport only if already stored on guests.
 */
export function shouldShowPassportOnHotelTicket(ticketData = {}) {
  const guests = Array.isArray(ticketData?.guests) ? ticketData.guests : [];
  let nationality =
    ticketData?.nationality ??
    ticketData?.hotelSearchData?.nationality ??
    "";
  if (!nationality && ticketData?.Request) {
    try {
      const req =
        typeof ticketData.Request === "string"
          ? JSON.parse(ticketData.Request)
          : ticketData.Request;
      nationality = req?.nationality ?? "";
    } catch {
      nationality = "";
    }
  }

  const rawNat = String(nationality ?? "").trim();
  // Explicit India → never Passport Number column (Pan No. as before).
  if (rawNat && isIndiaGuestNationality(rawNat)) {
    return false;
  }
  // Explicit non-India → Passport Number column.
  if (rawNat && isPassportRequiredByNationality(rawNat)) {
    return true;
  }
  // Nationality missing: only if passport already on guest rows.
  return guests.some(guestHasPassport);
}

/** Generate Voucher: international OR explicit panRequired rate (Domestic included).
 *  PAN Optional rate skips. Hold without PAN stays OK — collect PAN on ticket before pay.
 *  Non-Indian nationality (Passport Hold) never opens the PAN popup — go to payment. */
export function isPanRequiredForHotelVoucher(
  ticketData = {},
  { samePanForAll = false, membership = null } = {},
) {
  if (isMasterMemberAgent(5, membership)) {
    return false;
  }
  if (shouldShowPassportOnHotelTicket(ticketData)) {
    return false;
  }
  const panReq = resolveHotelBookingPanRequired(ticketData);
  if (panReq === false) {
    return false;
  }
  // Explicit PAN Required on booked rate → ask for Domestic + International.
  if (panReq !== true && !isInternationalHotelBooking(ticketData)) {
    return false;
  }
  if (isMultiRoomPanRequiredForHotelVoucher(ticketData)) {
    return !validateHotelTicketVoucherPan(ticketData, { samePanForAll, membership }).ok;
  }
  const guests = Array.isArray(ticketData?.guests) ? ticketData.guests : [];
  if (guests.length === 0) {
    return false;
  }
  return !guests.some(guestHasPan);
}

/** Canonical supplier free-cancel deadline for ticket/PDF (from roomResp policy or stored fields). */
export function resolveHotelTicketApiCancellationDeadline(ticketData = {}) {
  try {
    const raw = ticketData?.roomResp;
    if (raw) {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const fromRooms = resolveHotelBookingCancellationDeadlineFromRooms(
        parsed?.rooms ?? [],
      );
      if (fromRooms) {
        return fromRooms;
      }
    }
  } catch {
    /* ignore malformed roomResp */
  }

  const lcd = toHotelApiCancellationCalendarYmd(
    ticketData?.LastCancellationDate ?? "",
  );
  const lvd = toHotelApiCancellationCalendarYmd(
    ticketData?.LastVoucherDate ?? "",
  );
  if (lcd && lvd && lcd !== lvd) {
    return lcd >= lvd ? lcd : lvd;
  }
  return lcd || lvd || "";
}

/** Portal ticket/PDF display — API deadline minus 1 calendar day. */
export function formatHotelTicketCancellationDateForDisplay(
  ticketData,
  format = "DD-MM-YYYY",
) {
  const deadline = resolveHotelTicketApiCancellationDeadline(ticketData);
  return deadline
    ? formatHotelPortalCancellationDisplayDate(deadline, format)
    : "";
}

/** Portal ticket/PDF display — 1 calendar day before API / Tripjack free-cancel deadline. */
export function formatHotelLastCancellationDateForDisplay(
  date,
  format = "DD-MM-YYYY",
) {
  return formatHotelPortalCancellationDisplayDate(date, format);
}

export function formatHotelLastVoucherDateForDisplay(
  date,
  format = "DD-MM-YYYY",
) {
  return formatHotelPortalCancellationDisplayDate(date, format);
}

/** Last cancellation date still applies (same rule as ticket UI). */
export function isHotelLastCancellationDateActive(ticketData = {}) {
  const date = resolveHotelTicketApiCancellationDeadline(ticketData);
  if (!date) {
    return false;
  }
  return dayjs(`${date}T23:59:59`).isAfter(dayjs());
}

/** Hold / block booking before Generate Voucher payment. */
export function isHotelHoldAwaitingVoucher(ticketData = {}) {
  const status = Number(ticketData?.BookingStatus);
  const pgStatus = Number(ticketData?.pgStatus ?? 0);
  const purchaseType = String(ticketData?.PurchaseType ?? "").toLowerCase();
  return status === 10 && purchaseType === "block" && pgStatus === 0;
}

export function shouldShowHotelLastCancellationDate(ticketData = {}) {
  return (
    isHotelLastCancellationDateActive(ticketData) &&
    Boolean(resolveHotelTicketApiCancellationDeadline(ticketData))
  );
}

/** Pre-voucher hold only — hide after voucher is generated. */
export function shouldShowHotelLastVoucherDate(ticketData = {}) {
  return (
    isHotelHoldAwaitingVoucher(ticketData) &&
    shouldShowHotelLastCancellationDate(ticketData) &&
    Boolean(resolveHotelTicketApiCancellationDeadline(ticketData))
  );
}

/**
 * Non-refundable room/rate — cancel may succeed but wallet refund is 0.
 * Payment Status must stay "Paid" (never "Refunded") for these.
 */
export function isHotelTicketNonRefundable(ticketData = {}) {
  const rooms = Array.isArray(ticketData?.Rooms) ? ticketData.Rooms : [];
  for (const room of rooms) {
    const plans = Array.isArray(room?.ratePlans)
      ? room.ratePlans
      : Array.isArray(room?.RatePlans)
        ? room.RatePlans
        : [];
    for (const plan of plans) {
      if (
        plan?.refundable === false ||
        plan?.Refundable === false ||
        plan?.isRefundable === false
      ) {
        return true;
      }
    }
    if (
      room?.refundable === false ||
      room?.Refundable === false ||
      room?.isRefundable === false
    ) {
      return true;
    }
  }

  const blobs = [];
  for (const raw of [
    ticketData?.Request,
    ticketData?.roomResp,
    ticketData?.roomPlan,
  ]) {
    try {
      const s = typeof raw === "string" ? raw : JSON.stringify(raw ?? "");
      if (s) blobs.push(s);
    } catch {
      /* ignore */
    }
  }
  const joined = blobs.join(" ").toLowerCase();
  if (/non[\s_-]*refundable/.test(joined)) {
    return true;
  }
  if (
    /"refundable"\s*:\s*false/.test(joined) ||
    /"isrefundable"\s*:\s*false/.test(joined)
  ) {
    return true;
  }

  // Cancelled with no free-cancel deadline and no positive wallet credit → NR / full charges.
  const status = Number(ticketData?.BookingStatus ?? 0);
  const credited = Number(ticketData?.walletRefundAmount ?? 0) > 0;
  if (
    status === 6 &&
    !credited &&
    !resolveHotelTicketApiCancellationDeadline(ticketData)
  ) {
    return true;
  }

  return false;
}

/**
 * Payment Status: Refunded only when money actually returned on a refundable cancel.
 * Non-refundable cancel → always "Paid" (ticket / PDF / same helper).
 */
export function getHotelPaymentStatusText(ticketData = {}) {
  const pgStatus = Number(ticketData?.pgStatus ?? 0);
  if (pgStatus === 0) {
    return "Due";
  }
  if (isHotelTicketNonRefundable(ticketData)) {
    return "Paid";
  }
  const walletAmt = Number(ticketData?.walletRefundAmount ?? 0);
  const refundAmt = Number(ticketData?.refundAmount ?? 0);
  const moneyReturned =
    walletAmt > 0 ||
    (refundAmt > 0 && Boolean(ticketData?.walletRefunded)) ||
    refundAmt > 0;
  if (pgStatus === 1 && moneyReturned) {
    return "Refunded";
  }
  return "Paid";
}

/** Hide payment row on Hold/Pending/Blocked; hide "Due" on Confirmed/Vouchered/Cancelled (portal, PDF, email). */
export function shouldShowHotelPaymentStatus(ticketData = {}) {
  const status = Number(ticketData?.BookingStatus ?? 0);
  if ([2, 7, 10].includes(status)) {
    return false;
  }
  const paymentText = getHotelPaymentStatusText(ticketData);
  if (paymentText === "Due" && [3, 6, 11, 12].includes(status)) {
    return false;
  }
  return true;
}

/** Pending / Blocked — no download/email/markup sidebar (same as Flight & Activity). */
export function shouldShowHotelTicketSidebar(ticketData = {}) {
  const status = Number(getEffectiveHotelBookingStatus(ticketData));
  if (status === 7 || status === 2) {
    return false;
  }
  return true;
}

export function isHotelVoucheredStatus(ticketData = {}) {
  return Number(ticketData?.BookingStatus) === 11;
}

/** Hotel ticket sidebar — download panel title from booking status display. */
export function getHotelDownloadTicketHeader(ticketData = {}) {
  const status = getEffectiveHotelBookingStatus(ticketData);
  if (status === 10) {
    return "Download Confirmed Hotel";
  }
  if (status === 3 || status === 11) {
    return "Download Vouchered Hotel";
  }
  if (status === 6) {
    return "Download Cancelled Hotel";
  }
  return "Download Ticket";
}

export function isTripjackSupplierConfirmationNo(value = "") {
  return /^TJ/i.test(String(value ?? "").trim());
}

/** Admin manual / supplier confirmation for ticket, PDF, and email. */
export function getHotelConfirmationNumberForDisplay(ticketData = {}) {
  const confirmationNo = String(
    ticketData?.ConfirmationNumber ??
      ticketData?.ProviderConfirmationNo ??
      "",
  ).trim();
  if (!confirmationNo || confirmationNo === "0") {
    return "";
  }
  // Manual admin CNF (e.g. Ravi1234) — keep visible after cancel too.
  if (!isTripjackSupplierConfirmationNo(confirmationNo)) {
    return confirmationNo;
  }
  // Tripjack TJ… — only when vouchered and supplier reservation is loaded.
  if (!isHotelVoucheredStatus(ticketData)) {
    return "";
  }
  if (!ticketData?.supplierReservationLoaded) {
    return "";
  }
  return confirmationNo;
}

export function hasHotelConfirmationNumber(ticketData = {}) {
  return Boolean(getHotelConfirmationNumberForDisplay(ticketData));
}

/** PG transaction fee — same rules as Hotel-Review / HotelFairBox. */
export function computeHotelPgTransactionFee(
  transactionFee,
  payableBase,
  isPgSelected,
) {
  if (!isPgSelected || !transactionFee?.type) {
    return 0;
  }
  const feeType = String(transactionFee.type).toUpperCase();
  const feeAmount = parseFloat(transactionFee.amount ?? 0);
  const base = Number(payableBase) || 0;
  if (feeType === "P") {
    return (base / 100) * feeAmount;
  }
  if (feeType === "F") {
    return feeAmount;
  }
  return 0;
}

export function getHotelTicketPayableBase(ticketData, fareTotals = {}) {
  const fromLineItems = getHotelTicketDisplayTotal(ticketData, fareTotals, 0);
  if (fromLineItems > 0) {
    return fromLineItems;
  }
  if (isHotelAgentB2BTicket(ticketData)) {
    const agentFare = getHotelAgentTicketFareDisplay(ticketData);
    if (agentFare.hotelFareTotal > 0) {
      return agentFare.hotelFareTotal;
    }
  }
  const grossFromApi =
    Number(ticketData?.totalPrice ?? 0) + Number(ticketData?.postMarkup ?? 0);
  if (grossFromApi > 0) {
    if (isHotelAgentB2BTicket(ticketData)) {
      return Math.max(0, parseFloat(grossFromApi.toFixed(2)));
    }
    const commission = getHotelTicketDisplayCommission(ticketData);
    const tds = getHotelTicketDisplayTds(ticketData);
    return Math.max(
      0,
      parseFloat((grossFromApi - commission + tds).toFixed(2)),
    );
  }
  const fromFare = Number(fareTotals.grandTotal ?? 0);
  if (fromFare > 0) {
    return fromFare;
  }
  return 0;
}

/**
 * Ticket Total / Final Payment:
 * Agent: hotel fare + fees − promo (commission NOT deducted — matches Checkout Final Payment).
 * Guest: Base + Taxes + Other + fees − Commission − promo.
 */
export function getHotelTicketDisplayTotal(
  ticketData = {},
  fareTotals = {},
  transactionFee = 0,
) {
  const conv = Number(fareTotals.convienenceFee ?? 0);
  const insurance = Number(fareTotals.insuranceTotal ?? 0);
  const postMarkup = Number(ticketData?.postMarkup ?? 0);
  const promo = Number(fareTotals.discount ?? 0);
  const txFee = Number(transactionFee ?? 0);
  const isAgent = isHotelAgentB2BTicket(ticketData);

  if (isAgent) {
    const agentFare = getHotelAgentTicketFareDisplay(ticketData);
    const hotelFare = Number(agentFare.hotelFareTotal ?? 0);
    if (!(hotelFare > 0)) {
      return 0;
    }
    return Math.max(
      0,
      Math.round(hotelFare + conv + insurance + postMarkup + txFee - promo),
    );
  }

  const base = Number(fareTotals.baseAmount ?? 0);
  const tax = Number(fareTotals.taxAmount ?? 0);
  const other = Number(fareTotals.otherCharges ?? 0);
  const commission = getHotelTicketDisplayCommission(ticketData);

  if (!(base + tax > 0)) {
    return 0;
  }

  return Math.max(
    0,
    parseFloat(
      (
        base +
        tax +
        other +
        conv +
        insurance +
        postMarkup +
        txFee -
        promo -
        commission
      ).toFixed(2),
    ),
  );
}

/** Agent commission on hotel ticket (0 when none). */
export function getHotelTicketCommissionDiscount(ticketData = {}) {
  if (
    ticketData?.Commission !== undefined ||
    ticketData?.commission !== undefined
  ) {
    return Math.max(
      0,
      Number(ticketData?.Commission ?? ticketData?.commission ?? 0),
    );
  }
  const pg = Number(
    ticketData?.PgCommission ?? ticketData?.pgCommission ?? 0,
  );
  if (Number.isFinite(pg) && pg > 0) {
    return pg;
  }
  const rooms = Array.isArray(ticketData?.Rooms) ? ticketData.Rooms : [];
  let fromRooms = 0;
  rooms.forEach((room) => {
    fromRooms += Number(room?.commission ?? room?.Commission ?? 0);
  });
  return fromRooms > 0 ? fromRooms : 0;
}

/** TDS % on agent commission — matches checkout / Hotels TDS env default. */
export const HOTEL_AGENT_TDS_PERCENT = 2;

/**
 * Agent ticket/PDF/email fare — same rules as Checkout/Preview HotelFairBox:
 * Final hotel fare = room line total; Commission = DB% of that total (rounded);
 * TDS = 2% of commission (rounded); Taxes = API tax+other (rounded);
 * Room Price = hotel fare − Commission − TDS − Taxes.
 */
export function getHotelAgentTicketFareDisplay(ticketData = {}) {
  const rooms = Array.isArray(ticketData?.Rooms) ? ticketData.Rooms : [];
  let apiBaseFare = 0;
  let apiTax = 0;
  let apiOtherCharges = 0;
  let roomLineFromRooms = 0;

  rooms.forEach((room) => {
    apiBaseFare += Number(room?.base ?? 0);
    apiTax += Number(room?.tax ?? 0);
    apiOtherCharges += Number(room?.otherCharges ?? 0);
    const line = Number(room?.total ?? room?.Total ?? 0);
    if (line > 0) {
      roomLineFromRooms += line;
    }
  });

  const partsSum = apiBaseFare + apiTax + apiOtherCharges;
  const storedTotal = Math.round(Number(ticketData?.totalPrice ?? 0));
  const roomLineTotal = Math.max(
    0,
    Math.round(
      roomLineFromRooms > 0
        ? roomLineFromRooms
        : storedTotal > 0
          ? storedTotal
          : partsSum,
    ),
  );

  const apiCommission = getHotelTicketCommissionDiscount(ticketData);
  const apiTds = getHotelTicketTdsAmount(ticketData);
  const grossCommissionFromApi = Math.max(0, apiCommission + apiTds);
  const commissionRate =
    apiBaseFare > 0 ? grossCommissionFromApi / apiBaseFare : 0;
  const commission = Math.max(0, Math.round(roomLineTotal * commissionRate));
  const tds = Math.max(
    0,
    Math.round((commission * HOTEL_AGENT_TDS_PERCENT) / 100),
  );
  const taxes = Math.max(0, Math.round(apiTax + apiOtherCharges));
  const roomPrice = Math.max(0, roomLineTotal - commission - tds - taxes);

  return {
    hotelFareTotal: roomLineTotal,
    roomPrice,
    taxes,
    commission,
    tds,
  };
}

/** Commission for ticket/PDF display (matches checkout rounding). */
export function getHotelTicketDisplayCommission(ticketData = {}) {
  if (isHotelAgentB2BTicket(ticketData)) {
    return getHotelAgentTicketFareDisplay(ticketData).commission;
  }
  const raw = getHotelTicketCommissionDiscount(ticketData);
  return raw > 0 ? Math.round(raw) : 0;
}

export function isHotelAgentB2BTicket(ticketData = {}, isAgentUser = false) {
  return (
    Boolean(isAgentUser) || Number(ticketData?.RoleType ?? 0) === 5
  );
}

/** TDS on agent commission — shown under Other Charges on ticket/PDF/email. */
export function getHotelTicketTdsAmount(ticketData = {}) {
  if (ticketData?.TDS !== undefined || ticketData?.tds !== undefined) {
    return Math.max(0, Number(ticketData?.TDS ?? ticketData?.tds ?? 0));
  }
  const pg = Number(ticketData?.PgTds ?? ticketData?.pgTds ?? 0);
  if (Number.isFinite(pg) && pg > 0) {
    return pg;
  }
  const rooms = Array.isArray(ticketData?.Rooms) ? ticketData.Rooms : [];
  let fromRooms = 0;
  rooms.forEach((room) => {
    fromRooms += Number(room?.tds ?? room?.TDS ?? 0);
  });
  return fromRooms > 0 ? fromRooms : 0;
}

/** Ceil/round TDS for ticket/PDF/wallet display (matches checkout UI). */
export function getHotelTicketDisplayTds(ticketData = {}) {
  if (isHotelAgentB2BTicket(ticketData)) {
    return getHotelAgentTicketFareDisplay(ticketData).tds;
  }
  const raw = getHotelTicketTdsAmount(ticketData);
  return raw > 0 ? Math.round(raw) : 0;
}

export function getHotelPrice(ticketData) {
  let baseAmount = 0;
  let taxAmount = 0;
  let convienenceFee = 0;
  let discount = 0;
  let RefundAmount = 0;
  let insuranceTotal = 0;
  let totalPrice=0;
  let grandTotal = 0;
  let otherCharges = 0;

  let postMarkup = 0;
  if (ticketData?.postMarkup) {
    postMarkup = Number(ticketData?.postMarkup);
  }

  const checkin = new Date(ticketData.CheckInDate);
  const checkout = new Date(ticketData.CheckOutDate);
  const diffTime = Math.abs(checkout - checkin);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const noOfNights = Number(diffDays);
  const noOfRooms = Number(ticketData.NoOfRooms);
  const commissionDiscount = getHotelTicketDisplayCommission(ticketData);
  const tdsForFare = getHotelTicketDisplayTds(ticketData);

  if (Array.isArray(ticketData?.Rooms) && ticketData.Rooms.length > 0) {
    let totalBaseFare = ticketData.Rooms.reduce(
      (acc, val) => acc + val.base,
      0
    );

    // baseAmount = Number(noOfRooms * Number(totalBaseFare) * noOfNights);
    baseAmount = Number(totalBaseFare);
    let totalTax = ticketData.Rooms.reduce((acc, val) => acc + val.tax, 0);
    taxAmount = Number(totalTax);
    let totalOtherCharges = ticketData.Rooms.reduce(
      (acc, val) => acc + val.otherCharges,
      0
    );

    otherCharges = Number(totalOtherCharges);

    if (
      ticketData?.insuranceRequired === 1 &&
      ticketData?.insuranceData?.serviceType === 2
    ) {
      let totalPax = ticketData.Rooms.reduce(
        (acc, cur) => acc + Number(cur.adultCount) + Number(cur.childCount),
        0
      );

      insuranceTotal = totalPax * Number(ticketData?.insuranceData.amount);
    }
  }

  baseAmount = Number(baseAmount);
  const isAgentTicket = isHotelAgentB2BTicket(ticketData);
  // Agent checkout UI: TDS is a separate line — do not fold into Other Charges.
  otherCharges = isAgentTicket
    ? Number(otherCharges)
    : Number(otherCharges) + Number(tdsForFare);

  const roomSubtotal =
    Number(baseAmount) + Number(taxAmount) + Number(otherCharges);
  totalPrice = isAgentTicket
    ? roomSubtotal
    : roomSubtotal - commissionDiscount;

  if (ticketData?.convienenceData?.amount) {
    if (ticketData.convienenceData.type === 1) {
      convienenceFee = Number(ticketData.convienenceData.amount);
    } else {
      convienenceFee = Number(
        (Number(roomSubtotal) / 100) *
          Number(
            ticketData.convienenceData.amount
              ? ticketData.convienenceData.amount
              : 0
          )
      );
    }
  } else if (ticketData?.ConvienceData?.amount) {
    if (Number(ticketData.ConvienceData.type) === 1) {
      convienenceFee = Number(ticketData.ConvienceData.amount);
    } else {
      convienenceFee = Number(
        (Number(roomSubtotal) / 100) *
          Number(ticketData.ConvienceData.amount ?? 0),
      );
    }
  } else if (Number(ticketData?.Conveniencefee ?? 0) > 0) {
    convienenceFee = Number(ticketData.Conveniencefee);
  }

  if (ticketData.PromoData && ticketData?.PromoData?.DiscountType) {
    if (ticketData.PromoData.DiscountType == 1) {
      discount = Number((totalPrice / 100) * ticketData.PromoData.Discount);
    } else {
      discount = Number(ticketData.PromoData.Discount);
    }
  }
  RefundAmount = ticketData.RefundAmount ?? 0;

  grandTotal = Math.max(
    0,
    parseFloat(
      (
        roomSubtotal +
        Number(convienenceFee) +
        Number(insuranceTotal) +
        postMarkup -
        (isAgentTicket ? 0 : commissionDiscount) -
        Number(discount)
      ).toFixed(2),
    ),
  ).toFixed(2);

  return {
    baseAmount: Number(baseAmount).toFixed(2),
    taxAmount: Number(taxAmount).toFixed(2),
    otherCharges: Number(otherCharges).toFixed(2),
    convienenceFee: Number(convienenceFee).toFixed(2),
    discount: Number(discount).toFixed(2),
    RefundAmount: Number(RefundAmount).toFixed(2),
    grandTotal,
    insuranceTotal: Number(insuranceTotal).toFixed(2),
    noOfNights,
  };
}


export const getStatus = (status) => {
  switch (status) {
    case 4: {
      return <p style={{ color: "#FFA500" }}>FAILED</p>;
    }
    case 3: {
      // return <p style={{ color: "#008000" }}>CONFIRMED</p>;
       return <p style={{ color: "#FF0000" }}>VOUCHERED</p>;
    }
    case 6: {
      return <p style={{ color: "#FF0000" }}>CANCELLED</p>;
    }
    case 7: {
      return <p style={{ color: "#FF0000" }}>PENDING</p>;
    }
    case 5: {
      return <p style={{ color: "#f9e218" }}>REJECTED</p>;
    }
    case 10: {
      // Hold booking — portal shows CONFIRMED (same as before).
      return <p style={{ color: "#008000" }}>CONFIRMED</p>;
    }
    case 8: {
      return <p style={{ color: "#FF0000" }}>CANCELLATIONREQUESTED</p>;
    }
    case 9: {
      return <p style={{ color: "#FF0000" }}>CANCELLATIONPENDING</p>;
    }

    case 11: {
      return <p style={{ color: "#FF0000" }}>VOUCHERED</p>;
    }

    case 12: {
      return <p style={{ color: "#FF0000" }}>CANCELLATIONPROGRESS</p>;
    }
    case 2: {
      return <p style={{ color: "#FFA500" }}>BLOCKED</p>;
    }

    default:
      return;
  }
};
